<script setup lang="ts">
import { ref, nextTick, computed, watch } from 'vue';
import { useElementSize } from '@vueuse/core';
import ConnxTabButton from '@/components/Atoms/Buttons/ConnxTabButton.vue';
import type { ComponentPublicInstance } from 'vue';
import { DashboardTab } from '@/interfaces/Dashboard';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { InputType } from '@/interfaces/InputFields';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxHeaderQuickActions from '@/components/Molecules/ConnxHeaderQuickActions.vue';
import ConnxConfirmationModal from '@/components/Molecules/ConnxConfirmationModal.vue';
import ConnxHorizontalScroller from '@/components/Molecules/ConnxHorizontalScroller.vue';
import VueDraggable from 'vuedraggable';

const dashboardStore = useDashboardStore();

interface Props {
  editing: boolean;
}

const horizontalScrollerEl = ref();
const deleteConfirmationOpen = ref(false);
const tabName = ref();
const activeDashboardButton = ref<HTMLElement | null>(null);
const dashboardNavigation = ref();
const dashboardNavigationContainer = ref();
const { width: dashboardNavigationWidth } = useElementSize(dashboardNavigation);
const { width: dashboardNavigationContainerWidth } = useElementSize(dashboardNavigationContainer);

const MAX_NAVIGATION_WIDTH_BEFORE_WRAP = 900;

const dashboardNavWidth = computed(() => {
  if (dashboardNavigationContainerWidth.value <= MAX_NAVIGATION_WIDTH_BEFORE_WRAP) {
    return dashboardNavigationContainerWidth.value + 300;
  }
  return dashboardNavigationWidth.value - 300;
});

const deleteDashboardTab = async () => {
  toggleDeleteConfirmationOpen();
  await dashboardStore.removeActiveDashboard();
  emit('disable-edit');
};

const toggleDeleteConfirmationOpen = () => {
  deleteConfirmationOpen.value = !deleteConfirmationOpen.value;
};

const scrollToActiveDashboardTab = () => {
  const scrollContainer = horizontalScrollerEl.value?.scrollContainer;
  const active = activeDashboardButton.value;

  if (!scrollContainer || !active) return;

  const containerRect = scrollContainer.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();

  const containerWidth = scrollContainer.clientWidth;

  const activeCenterOffset = activeRect.left - containerRect.left + activeRect.width / 2;

  const targetScrollLeft = scrollContainer.scrollLeft + activeCenterOffset - containerWidth / 2;

  const maxScroll = scrollContainer.scrollWidth - containerWidth;
  const safeScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScroll));

  horizontalScrollerEl.value?.scrollByTotal(safeScrollLeft);
};

const emit = defineEmits(['disable-edit', 'toggle-edit', 'add-new-dashboard']);

const props = defineProps<Props>();

const setActiveDashboardButtonRef = (el: Element | ComponentPublicInstance | null) => {
  if (!el) return;
  activeDashboardButton.value = getElementFromRef(el);
  scrollToActiveDashboardTab();
};

const getElementFromRef = (el: any) => {
  if (el instanceof HTMLElement) {
    return el;
  } else if ('$el' in el && el.$el instanceof HTMLElement) {
    return el.$el;
  }
};

const saveDashboardTab = async () => {
  try {
    const activeDashboard = dashboardStore.activeDashboardTab;
    if (!activeDashboard) return;
    activeDashboard.dashboardTabName = tabName.value;

    const success = await dashboardStore.saveDashboardTab(activeDashboard);
    if (success) {
      emit('toggle-edit');
      await nextTick();
    }
  } catch (error) {
    console.error('[DashboardComponent] Failed to save dashboard tab:', error);
  }
};

const cancelDashboardChanges = async () => {
  if (dashboardStore.activeDashboardTab) {
    const dashboardTabWidgets = await dashboardStore.fetchDashboardTabWidgets(dashboardStore.activeDashboardTab.guid!);
    dashboardStore.activeDashboardTab.dashboardTabWidgets = dashboardTabWidgets;
  }
  emit('toggle-edit');
};

const setActiveDashboard = async (id: any) => {
  dashboardStore.setActiveDashboardTab(id);
};

const dashboardTitles = computed<DashboardTab[]>({
  get() {
    return dashboardStore.dashboardTabs
      .map((i) => ({
        dashboardTabName: i.dashboardTabName,
        isCurrent: i.isCurrent,
        sortOrder: i.sortOrder,
        synced: i.synced,
        guid: i.guid,
      }))
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  },
  set(newOrderedDashboards) {
    const reorderedTabs = newOrderedDashboards.map((tab, index) => {
      const fullTab = dashboardStore.dashboardTabs.find((t) => t.guid === tab.guid);

      return {
        ...fullTab,
        sortOrder: index,
      } as DashboardTab;
    });

    dashboardStore.updateDashboardTabOrder(reorderedTabs);
  },
});

const errors = ref(new Map<string, string | null>());

const onFieldError = ({ name, error }: { name: string; error: string | null }) => {
  if (error) {
    errors.value.set(name, error);
  } else {
    errors.value.delete(name);
  }
};

const addNewDashboardTab = async () => {
  await dashboardStore.addNewDashboardTab();
  emit('toggle-edit');
  await nextTick();
};

watch(
  () => props.editing,
  () => {
    tabName.value = dashboardStore?.activeDashboardTab?.dashboardTabName;
  }
);

defineExpose({ scrollToActiveDashboardTab });
</script>

<template>
  <div class="tw-bg-connx-white tw-px-8 tw-pt-8 tw-border-solid tw-border-b tw-gap-4">
    <div
      class="tw-flex tw-flex-wrap laptop:tw-flex-nowrap tw-items-end"
      ref="dashboardNavigationContainer"
    >
      <div
        ref="dashboardNavigation"
        class="tw-w-full tw-max-w-none"
      >
        <div
          class="tw-flex tw-justify-between tw-flex-grow tw-flex-wrap tw-gap-y-4"
          :class="{
            ' tw-flex-col-reverse': dashboardNavigationContainerWidth < MAX_NAVIGATION_WIDTH_BEFORE_WRAP,
          }"
        >
          <div
            class="tw-gap-x-4"
            :class="{
              'tw-flex tw-items-end  tw-grow laptop:tw-grow-0':
                dashboardNavigationContainerWidth >= MAX_NAVIGATION_WIDTH_BEFORE_WRAP,
              'tw-w-full': dashboardNavigationContainerWidth < MAX_NAVIGATION_WIDTH_BEFORE_WRAP,
            }"
          >
            <ConnxButton
              button-style="secondary"
              v-if="dashboardNavigationContainerWidth >= MAX_NAVIGATION_WIDTH_BEFORE_WRAP"
              class="tw-mb-2"
              icon="fas fa-home-alt"
              icon-position="before"
              link="frmWelcome.aspx?redirect=false"
            >Welcome</ConnxButton>
            <connx-horizontal-scroller
              ref="horizontalScrollerEl"
              v-show="dashboardStore.activeDashboardTab"
              :width="dashboardNavWidth"
            >
              <vue-draggable
                handle=".vd-handle"
                ref="activeDashboardScrollContainer"
                :disabled="!editing"
                tag="ul"
                class="tw-flex tw-mx-1"
                v-model="dashboardTitles"
                item-key="guid"
                :animation="150"
              >
                <template #item="{ element }">
                  <li class="tw-ml-1">
                    <ConnxTabButton
                      :disabled="editing && !element.isCurrent"
                      :class="{
                        'tw-cursor-move vd-handle': editing && !element.isCurrent,
                        'tw-pt-2 tw-pb-2 tw-flex': editing && element.isCurrent,
                        'tw-pt-4': errors.has('tab-name'),
                      }"
                      :as="editing ? 'div' : 'button'"
                      :active="element.isCurrent"
                      :ref="element.isCurrent ? setActiveDashboardButtonRef : undefined"
                      @click="setActiveDashboard(element.guid)"
                    >
                      <div
                        v-if="editing && element.isCurrent"
                        class="vd-handle tw-bg-transparent tw-opacity-100 tw-border-none tw-outline-none tw-cursor-move tw-flex tw-items-center tw-justify-center tw-p-0 tw-pr-2 tw-text-connx-grey"
                      >
                        <connx-icon name="far fa-arrows-alt"></connx-icon>
                      </div>

                      <connx-field
                        v-if="editing && element.isCurrent"
                        v-model="tabName"
                        :required="true"
                        @update:error="onFieldError"
                        :type="InputType.Text"
                        :maxlength="100"
                        @mousedown.stop
                        @touchstart.stop
                        absolute-error
                        name="tab-name"
                        placeholder="Dashboard Tab Name"
                        class="tw-mb-0 tw-min-w-52"
                      >
                      </connx-field>
                      <span v-else>
                        {{ element.dashboardTabName }}
                      </span>
                    </ConnxTabButton>
                  </li>
                </template>
              </vue-draggable>
            </connx-horizontal-scroller>
          </div>
          <div class="tw-flex tw-justify-between tw-gap-x-4">
            <ConnxButton
              button-style="secondary"
              v-if="dashboardNavigationContainerWidth < MAX_NAVIGATION_WIDTH_BEFORE_WRAP"
              class="tw-mb-2"
              icon="fas fa-home-alt"
              icon-position="before"
              link="frmWelcome.aspx?redirect=false"
            >Welcome
            </ConnxButton>
            <ul
              class="tw-flex tw-ml-4 tw-mt-1"
              v-show="dashboardStore.activeDashboardTab"
            >
              <li
                v-if="editing && dashboardStore.activeDashboardTab"
                class="tw-ml-2"
              >
                <ConnxButton
                  button-style="danger-alt"
                  icon-position="before"
                  icon="far fa-trash-alt"
                  @click="toggleDeleteConfirmationOpen()"
                >
                  Delete
                </ConnxButton>
              </li>
              <li
                v-if="editing && dashboardStore.activeDashboardTab"
                class="tw-ml-2"
              >
                <ConnxButton
                  icon-position="before"
                  @click="cancelDashboardChanges"
                > Cancel </ConnxButton>
              </li>
              <li
                v-if="editing"
                class="tw-ml-2"
              >
                <ConnxButton
                  title="Save"
                  icon-position="before"
                  button-style="success"
                  icon="fas fa-check"
                  :disabled="tabName === ''"
                  @click="saveDashboardTab"
                >
                  Save
                </ConnxButton>
              </li>
              <li
                class="tw-ml-2"
                v-if="!editing"
              >
                <ConnxButton
                  button-style="circular"
                  class="hover:tw-bg-connx-primary hover:tw-text-connx-button-text hover:tw-border-connx-primary"
                  @click="addNewDashboardTab"
                  :disabled="editing"
                  title="Add New Dashboard"
                >
                  <ConnxIcon
                    name="fas fa-plus"
                    class="tw-w-auto tw-h-[17px]"
                  />
                </ConnxButton>
              </li>
              <li
                class="tw-ml-2"
                v-if="!editing"
              >
                <ConnxButton
                  title="Edit"
                  button-style="circular"
                  class="tw-text-connx-grey"
                  @click="emit('toggle-edit')"
                >
                  <ConnxIcon
                    name="fas fa-pen"
                    class="tw-w-auto tw-h-[17px]"
                  />
                </ConnxButton>
              </li>
              <li class="tw-ml-1">
                <ConnxHeaderQuickActions disable-print />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>

  <connx-confirmation-modal
    :open="deleteConfirmationOpen"
    confirmation-button-text="Yes, delete it."
    @confirmed="deleteDashboardTab()"
    @close="toggleDeleteConfirmationOpen()"
  >
  </connx-confirmation-modal>
</template>
